/* eslint-disable no-console */
import {useInfiniteQuery} from '@tanstack/react-query';
import {
	FirstNetworkTypeEnum,
	NetworkTypeEnum,
} from 'domain/usecases/private/guide-flow/medical-guide/get-providers-v3';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {useHostName} from 'presentation/hooks';
import {GuideStep} from 'presentation/modules/guide/shared/constants/constants';
import {useAccountInformation, useQueryParams} from 'presentation/pages/private';
import {useDebounce} from 'presentation/pages/private/hooks/useDebounce';
import {getHealthProvidersV3} from '../../services/getHealthProvidersV3';
import {HealthGuideServicesTypeEnum} from 'presentation/constants';
import {usePpoNetworkStore} from 'presentation/modules/guide/shared/store/guideStore/usePpoNetworkStore';
import {ppoNetworkTypeEnum} from 'presentation/modules/guide/shared/store/guideStore/types';

export function useHealthProvidersV3Query() {
	const {SEARCH_PARAMS} = useQueryParams();
	const {accesses} = useAccountInformation();
	const {isGndiSaoPaulo, isNotredameSaude} = useHostName();
	const ppoNetworkTypeState = usePpoNetworkStore(state => state.ppoNetworkType);

	const {
		ufCode,
		cityName,
		coordinates,
		serviceType,
		specialtyName,
		cd_procedimento,
		networkCode,
		distance,
		step,
		query,
	} = SEARCH_PARAMS;

	const firstNetworkType = () => {
		if (tpRede !== NetworkTypeEnum.ALL) {
			return undefined;
		}

		return isPpo ? FirstNetworkTypeEnum.ACCREDITED_NETWORK : FirstNetworkTypeEnum.SELF_NETWORK;
	};

	const getNetworkCode = () => {
		if (isNotredameSaude) {
			if (ppoNetworkTypeState === ppoNetworkTypeEnum.SELF_NETWORK) {
				return 1;
			}

			if (ppoNetworkTypeState === ppoNetworkTypeEnum.ACCREDITED_NETWORK) {
				return 2;
			}
		}

		return isGndiSaoPaulo ? networkType : undefined;
	};

	const debouncedSearch = useDebounce({value: query, delay: 400});
	const isPpo = accesses?.ppoProduct ?? false;
	const tpRede = networkCode ? Number(networkCode) : undefined;
	const coordinatesParam = coordinates || undefined;
	const cdProcedimento = cd_procedimento || undefined;
	const providerFilter = query !== '' && debouncedSearch !== '' ? debouncedSearch : undefined;
	const networkType = isPpo ? tpRede : NetworkTypeEnum.ALL;
	const isAuxiliaryService = serviceType === HealthGuideServicesTypeEnum.AUXILIARY_SERVICES;

	const params = {
		coordenadas: coordinatesParam,
		cdUf: ufCode,
		nmCidade: cityName,
		dsTipo: serviceType,
		dsEspecialidade: specialtyName,
		cd_procedimento: cdProcedimento,
		retornoAgrupado: false,
		tpRede: getNetworkCode(),
		firstNetworkType: firstNetworkType(),
		nmPrestador: providerFilter,
		distancia: distance,
		limit: isAuxiliaryService ? 24 : 12,
	};

	const {
		data: infiniteData,
		isLoading,
		isFetching,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
		isError,
		refetch,
	} = useInfiniteQuery({
		queryKey: [QUERY_KEYS.HEALTH_PROVIDERS_GUIDE, params],
		queryFn: async ({pageParam, signal}) =>
			getHealthProvidersV3({...params, page: pageParam, signal}),
		initialPageParam: 1,
		getNextPageParam: lastPage => {
			if (!lastPage || !lastPage.hasNextPage) {
				return undefined;
			}
			return lastPage.pageNumber + 1;
		},
		enabled:
			Boolean((ufCode && cityName) || coordinates) && Boolean(step === GuideStep.RESULTS),
	});

	const providers = infiniteData?.pages.flatMap(page => page.prestadores) ?? [];

	return {
		data: providers,
		isLoading,
		isFetching,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
		isError,
		refetch,
		loadMore: () => {
			if (hasNextPage && !isFetchingNextPage) {
				fetchNextPage();
			}
		},
	};
}
